import { useRef, useState, useCallback, useEffect } from "react";
import { MIN_SCALE, MAX_SCALE } from "./ScalingTool.constants";
import { useIntl } from "react-intl";

const SCALE_STEP = 25;
const DEBOUNCE_TIME = 500;

type ScalingToolProps = {
  initialScale: number | null;
  setScale: (scale: number) => void;
};

export const useScalingTool = ({
  initialScale,
  setScale
}: ScalingToolProps) => {
  const [currentScale, setCurrentScale] = useState((initialScale ?? 1) * 100);
  const debouncedTimeoutId = useRef<NodeJS.Timeout | null>(null);

  const handleChange = useCallback(
    (value: number) => {
      setCurrentScale(value);
      if (debouncedTimeoutId.current) {
        clearTimeout(debouncedTimeoutId.current);
      }
      debouncedTimeoutId.current = setTimeout(() => {
        setScale(Math.min(Math.max(value, MIN_SCALE), MAX_SCALE) / 100);
      }, DEBOUNCE_TIME);
    },
    [setScale]
  );

  useEffect(() => {
    setCurrentScale((initialScale ?? 1) * 100);
  }, [initialScale]);

  const handleInputChange = useCallback(
    (inputValue: string) => {
      const value = parseInt(inputValue.replace("%", ""), 10);
      if (!isNaN(value)) {
        handleChange(value);
      }
    },
    [handleChange]
  );

  const decreaseScale = useCallback(() => {
    handleChange(currentScale - SCALE_STEP);
  }, [currentScale, handleChange]);

  const increaseScale = useCallback(() => {
    handleChange(currentScale + SCALE_STEP);
  }, [currentScale, handleChange]);

  const hasError = currentScale < MIN_SCALE || currentScale > MAX_SCALE;
  return {
    currentScale,
    handleInputChange,
    decreaseScale,
    increaseScale,
    hasError
  };
};

export const useScalingText = () => {
  const intl = useIntl();
  const scalePercentageText = intl.formatMessage({
    id: "scaling.percentage",
    defaultMessage: "Scale percentage"
  });
  const decreaseScaleText = intl.formatMessage({
    id: "scaling.decrease",
    defaultMessage: "Decrease scale"
  });
  const increaseScaleText = intl.formatMessage({
    id: "scaling.increase",
    defaultMessage: "Increase scale"
  });

  const scaleMinMaxErrorText = intl.formatMessage(
    {
      id: "scaling.minmax",

      defaultMessage: `Scale must be between ${MIN_SCALE}% and ${MAX_SCALE}%`
    },
    {
      min: MIN_SCALE,
      max: MAX_SCALE
    }
  );

  return {
    scalePercentageText,
    decreaseScaleText,
    increaseScaleText,
    scaleMinMaxErrorText
  };
};
